import PropTypes from "prop-types";
import { Divider, Grid } from "@mui/material";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import BaseEnum from "enums/BaseEnum";
import { formatStatusText } from "utils/statusFormatter";

const formatDisplayValue = (value) =>
  value === null || value === undefined || value === "" ? "N/A" : value;

const InfoItem = ({ label, value }) => {
  const isCurrency =
    typeof label === "string" && label.toLocaleLowerCase("tr-TR").includes("fiyat");
  const formattedValue =
    formatDisplayValue(value) === "N/A"
      ? "N/A"
      : isCurrency && !`${value}`.includes("₺")
      ? `${value} ₺`
      : value;
  return (
    <Grid item xs={12} md={6}>
      <MDTypography variant="body2" color="text">
        <strong>{label}:</strong> {formattedValue}
      </MDTypography>
    </Grid>
  );
};

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const CommentBlock = ({ label, value }) => (
  <MDBox mb={1.5}>
    <MDTypography variant="button" fontWeight="medium" color="text">
      {label}
    </MDTypography>
    <MDTypography
      variant="body2"
      color="text"
      sx={{
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        whiteSpace: "normal",
      }}
    >
      {formatDisplayValue(value)}
    </MDTypography>
  </MDBox>
);

CommentBlock.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function RequestDetailContent({ request, isCustomer, isResource, isLogistics }) {
  if (!request) return null;

  const customer = request.customer;
  const factoryInfo = request.fabrikaInfo;
  const factory = customer?.fabrikalar?.[0];
  const displayStatus = isCustomer ? request.customerStage || request.status : request.status;
  const hideExtraInfo =
    !isCustomer ||
    (displayStatus === "İncelemede" &&
      Number(request.price) === 535353 &&
      Number(request.pricePerTon) === 4646);
  const isPaymentPendingStage =
    (request.customerStage || "").toLocaleLowerCase("tr-TR") === "ödeme bekleniyor";
  const showExpectedPaymentDate = isPaymentPendingStage && request.expectedPaymentDate;
  const showManagerComment = !isCustomer;
  const showResourceComment = !isCustomer;

  const commentSummary = (
    <MDBox mt={3}>
      <CommentBlock label="Sipariş Yorumu" value={request.comment} />
      {showManagerComment && (
        <CommentBlock label="Yönetici Yorumu" value={request.managerComment} />
      )}
    </MDBox>
  );

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return value;
      }
      return date.toLocaleDateString("tr-TR");
    } catch (error) {
      return value;
    }
  };

  const formatKilograms = (value, totalTons) => {
    if (typeof totalTons === "number" && !Number.isNaN(totalTons)) {
      const kilograms = totalTons * 1000;
      return `${kilograms.toLocaleString("tr-TR")} kg`;
    }
    if (typeof value === "number" && !Number.isNaN(value)) {
      return `${value.toLocaleString("tr-TR")} kg`;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
    return "N/A";
  };

  if (isLogistics) {
    return (
      <MDBox>
        {commentSummary}
        <MDBox mt={3}>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Talep Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Talep ID" value={request.id} />
            <InfoItem label="Geliş Tarihi" value={formatDate(request.requestedDeliveryDate)} />
            <InfoItem label="Miktar" value={request.amount} />
            <InfoItem
              label="Toplam Kilogram"
              value={formatKilograms(request.amount, request.totalTons)}
            />
            <InfoItem label="Fabrika Adı" value={factoryInfo?.fabrikaAdi} />
            <InfoItem label="Fabrika Adresi" value={factoryInfo?.adres} />
            <InfoItem label="Kaynak Durum" value={request.status} />
            <InfoItem label="Lojistik Durum" value={formatStatusText(request.logisticsStatus)} />
          </Grid>
          {showResourceComment && (
            <CommentBlock label="Maden Yorumu" value={request.resourceComment} />
          )}
        </MDBox>
      </MDBox>
    );
  }

  if (isResource) {
    return (
      <MDBox>
        {commentSummary}
        <MDBox mt={3}>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Müşteri Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Ticari Ünvan" value={customer?.ticariUnvan} />
          </Grid>
        </MDBox>

        <Divider sx={{ my: 3 }} />

        <MDBox>
          <MDTypography variant="h6" fontWeight="medium" gutterBottom>
            Talep Bilgileri
          </MDTypography>
          <Grid container spacing={2}>
            <InfoItem label="Sipariş ID" value={request.orderFormId ?? request.id} />
            <InfoItem label="Teslim Tarihi" value={formatDate(request.requestedDeliveryDate)} />
            <InfoItem label="Durum" value={displayStatus} />
            <InfoItem label="Talep Edilen Miktar" value={request.amount} />
            <InfoItem label="Sınıflandırma" value={request.classification} />
            <InfoItem label="Tuz Türü" value={request.materialType} />
            <InfoItem label="Tuz Boyutu" value={request.size} />
            <InfoItem label="Paketleme" value={request.packaging} />
          </Grid>
          {showResourceComment && (
            <CommentBlock label="Maden Yorumu" value={request.resourceComment} />
          )}
        </MDBox>
      </MDBox>
    );
  }

  return (
    <MDBox>
      {commentSummary}
      {!isCustomer && (
        <>
          <MDBox mt={3}>
            <MDTypography variant="h6" fontWeight="medium" gutterBottom>
              Genel Bilgiler
            </MDTypography>
            <Grid container spacing={2}>
              <InfoItem label={BaseEnum.TRADE_NAME} value={customer?.ticariUnvan} />
              <InfoItem label={BaseEnum.PHONE} value={customer?.telefon} />
              <InfoItem label={BaseEnum.EMAIL} value={customer?.eposta} />
              <InfoItem label={BaseEnum.SECTOR} value={customer?.sector} />
              <InfoItem label={BaseEnum.TAX_OFFICE} value={customer?.vergiDairesi} />
              <InfoItem label={BaseEnum.TAX_NUMBER} value={customer?.vergiNumarasi} />
              <InfoItem label={BaseEnum.ADDRESS} value={customer?.adres} />
            </Grid>
          </MDBox>

          <Divider sx={{ my: 3 }} />
        </>
      )}

      <MDBox>
        <MDTypography variant="h6" fontWeight="medium" gutterBottom>
          Fabrika Bilgileri
        </MDTypography>
        <Grid container spacing={2}>
          <InfoItem label={BaseEnum.FACTORY_NAME} value={factory?.fabrikaAdi} />
          <InfoItem label={BaseEnum.FACTORY_MANAGER} value={factory?.fabrikaSorumlusu} />
          <InfoItem label={BaseEnum.FACTORY_PHONE} value={factory?.fabrikaTelefon} />
          <InfoItem label={BaseEnum.FACTORY_ADDRESS} value={factory?.adres} />
        </Grid>
      </MDBox>

      <Divider sx={{ my: 3 }} />

      <MDBox>
        <MDTypography variant="h6" fontWeight="medium" gutterBottom>
          Talep Bilgileri
        </MDTypography>
        <Grid container spacing={2}>
          <InfoItem label={BaseEnum.PRICE} value={request.price?.toString()} />
          <InfoItem label={BaseEnum.AMOUNT} value={request.amount} />
          <InfoItem label={BaseEnum.MATERIAL_TYPE} value={request.materialType} />
          <InfoItem label={BaseEnum.PAYMENT_TYPE} value={request.paymentType} />
          <InfoItem label={BaseEnum.TERM} value={request.term} />
          {showExpectedPaymentDate && (
            <InfoItem
              label={BaseEnum.EXPECTED_PAYMENT_DATE}
              value={formatDate(request.expectedPaymentDate)}
            />
          )}
          <InfoItem label={BaseEnum.SIZE} value={request.size} />
          <InfoItem label={BaseEnum.CLASSIFICATION} value={request.classification} />
          <InfoItem label={BaseEnum.PACKAGING} value={request.packaging} />
          <InfoItem
            label={BaseEnum.REQUESTED_DELIVERY_DATE}
            value={formatDate(request.requestedDeliveryDate)}
          />
          <InfoItem label={BaseEnum.PRICE_PER_TON} value={request.pricePerTon?.toString()} />
          <InfoItem label={BaseEnum.STATUS} value={displayStatus} />
          <InfoItem label={BaseEnum.RESPONSIBLE_USER} value={request.responsibleUserName} />
        </Grid>
        {showResourceComment && (
          <CommentBlock label="Maden Yorumu" value={request.resourceComment} />
        )}
      </MDBox>

      {!isCustomer && (
        <>
          <Divider sx={{ my: 3 }} />
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" gutterBottom>
              Lojistik
            </MDTypography>
            <Grid container spacing={2}>
              <InfoItem
                label={BaseEnum.LOGISTICS_STATUS}
                value={formatStatusText(request.logisticsStatus)}
              />
              <InfoItem label={BaseEnum.LOGISTICS_PRICE} value={request.logisticsPrice} />
            </Grid>
            <CommentBlock label={BaseEnum.LOGISTICS_COMMENT} value={request.logisticsComment} />
          </MDBox>
        </>
      )}

      {/* Ek Bilgiler intentionally hidden for all users */}
    </MDBox>
  );
}

RequestDetailContent.propTypes = {
  request: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customer: PropTypes.shape({
      name: PropTypes.string,
      ticariUnvan: PropTypes.string,
      adres: PropTypes.string,
      vergiDairesi: PropTypes.string,
      vergiNumarasi: PropTypes.string,
      eposta: PropTypes.string,
      telefon: PropTypes.string,
      sector: PropTypes.string,
      fabrikalar: PropTypes.arrayOf(
        PropTypes.shape({
          fabrikaAdi: PropTypes.string,
          fabrikaSorumlusu: PropTypes.string,
          fabrikaTelefon: PropTypes.string,
          adres: PropTypes.string,
        })
      ),
    }),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    amount: PropTypes.string,
    totalTons: PropTypes.number,
    materialType: PropTypes.string,
    paymentType: PropTypes.string,
    term: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    size: PropTypes.string,
    classification: PropTypes.string,
    packaging: PropTypes.string,
    requestedDeliveryDate: PropTypes.string,
    comment: PropTypes.string,
    managerComment: PropTypes.string,
    resourceComment: PropTypes.string,
    orderFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
    createdById: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pricePerTon: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    expectedPaymentDate: PropTypes.string,
    logisticsStatus: PropTypes.string,
    logisticsComment: PropTypes.string,
    logisticsPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customerStage: PropTypes.string,
    responsibleUserName: PropTypes.string,
    fabrikaInfo: PropTypes.shape({
      fabrikaAdi: PropTypes.string,
      adres: PropTypes.string,
    }),
  }),
  isCustomer: PropTypes.bool,
  isResource: PropTypes.bool,
  isLogistics: PropTypes.bool,
};

RequestDetailContent.defaultProps = {
  isCustomer: false,
  isResource: false,
  isLogistics: false,
};

export default RequestDetailContent;
